<template>
  <v-card :disabled="loading" flat>
    <!-- actions top -->
    <v-card-text class="d-flex align-items-center">
      <v-btn
        :disabled="!hasMissingDefaults"
        @click="addDefaults"
        outlined
        color="green"
      >
        <v-icon>mdi-plus</v-icon>
        {{ $t("add_default_form") }}
      </v-btn>

      <v-btn @click="deleted('all')" class="mr-2" outlined color="red">
        <v-icon>mdi-delete</v-icon>
        {{ $t("delete_all") }}
      </v-btn>

      <v-switch
        :disabled="!!info"
        v-model="conform"
        :label="conform ? $t('validation_active') : $t('validation_inactive')"
      >
        {{ $t("validation") }}
      </v-switch>
    </v-card-text>

    <!-- listed item -->
    <v-list>
      <v-row>
        <v-col
          v-for="(item, indexOf) in items"
          :key="indexOf"
          cols="12"
          md="6"
          class="pa-2"
        >
          <v-list-group
            v-model="item.active"
            :prepend-icon="item.action"
            no-action
            :ripple="false"
            class="border pa-2"
            style="border-radius: 15px; box-shadow: none !important"
            :key="$i18n.locale + indexOf"
          >
            <template v-slot:activator>
              <v-list-item-content>
                <v-list-item-title v-text="item.title" />
              </v-list-item-content>
            </template>

            <v-list-item
              v-for="(child, indexChild) in item.items"
              :key="child.title"
              class="pa-2 d-flex"
            >
              <v-list-item-content class="d-flex pa-4">
                <v-list-item-title>
                  <v-row>
                    <v-col cols="12">
                      <v-btn
                        @click="deleted(item.id)"
                        outlined
                        class="w-100"
                        color="red"
                      >
                        <v-icon>mdi-delete</v-icon>
                        {{ $t("clear_form") }}
                      </v-btn>
                    </v-col>
                    <v-col
                      cols="12"
                      class="d-flex align-items-center justify-content-start"
                    >
                      {{ $t("status") }}
                      <div class="d-flex align-center" style="cursor: pointer">
                        <v-checkbox
                          v-model="child.action"
                          hide-details
                          :label="child.action ? $t('active') : $t('inactive')"
                          class="ma-0 pa-0"
                          dense
                          style="height: 20px; width: 20px"
                          @click.stop
                        />
                      </div>
                    </v-col>

                    <v-col class="pa-0" cols="12" md="6">
                      <v-card-subtitle v-text="child.title" />
                    </v-col>

                    <v-col class="pa-0" cols="12" md="6">
                      <v-radio-group v-model="child.optional" mandatory>
                        <v-radio :label="$t('optional')" :value="false" />
                        <v-radio :label="$t('required')" :value="true" />
                      </v-radio-group>
                    </v-col>

                    <v-col cols="12" v-if="child.type === 'select'">
                      <v-text-field
                        v-model="child.label"
                        class="mb-2"
                        dense
                        persistent-hint
                        :label="$t('question_title')"
                        outlined
                        :hint="$t('question_title_hint')"
                      />

                      <v-combobox
                        v-model="child.selectItems"
                        chips
                        dense
                        :hint="$t('options_hint')"
                        persistent-hint
                        :label="$t('options')"
                        multiple
                        outlined
                      >
                        <template
                          v-slot:selection="{ attrs, item, select, selected }"
                        >
                          <v-chip
                            :input-value="selected"
                            close
                            small
                            v-bind="attrs"
                            @click="select"
                            @click:close="removeData(item, indexOf, indexChild)"
                          >
                            {{ item }}
                          </v-chip>
                        </template>
                      </v-combobox>
                    </v-col>
                  </v-row>
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list-group>
        </v-col>
      </v-row>
    </v-list>

    <!-- action bottom -->
    <v-card-actions>
      <v-btn
        v-if="!changed"
        :loading="loading"
        @click="dialog = true"
        class="w-100"
        color="green"
        outlined
      >
        <v-icon>mdi-plus</v-icon>
        {{ $t("add_suggested_form") }}</v-btn
      >
      <v-btn
        @click="update"
        class="w-100"
        v-else
        :loading="loading"
        outlined
        color="orange"
      >
        {{ $t("save_changes") }}
        <v-icon>mdi-reload</v-icon>
      </v-btn>
    </v-card-actions>

    <!-- modal of add -->
    <custom-popup
      @action="addNewItem"
      max-width="500px"
      max-height="500px"
      v-model="dialog"
      :cons="{ title: $t('add_request_field') }"
    >
      <v-card class="pa-2" flat style="overflow-x: hidden">
        <v-row class="pa-0" flat>
          <v-col cols="12" md="6">
            <icon-picker v-model="newItem.action" />
          </v-col>
          <v-col cols="12" md="6">
            <v-combobox
              v-model="newItem.items[0].type"
              dense
              outlined
              :placeholder="$t('select_form_type_placeholder')"
              :label="$t('select_form_type')"
              :items="[
                { value: 'select', text: $t('dropdown') },
                { value: 'text', text: $t('text_field') },
                { value: 'number', text: $t('number_field') },
                { value: 'textarea', text: $t('textarea_field') },
                { value: 'date', text: $t('date_field') },
              ]"
              item-value="value"
              item-text="text"
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              :rules="[(v) => !!v || v == null || $t('field_name_required')]"
              :label="$t('field_name')"
              :placeholder="$t('add_field_name')"
              outlined
              dense
              v-model="newItem.title"
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              :rules="[(v) => !!v || v == null || $t('field_name_required')]"
              :label="$t('field_label')"
              :placeholder="$t('add_field_label')"
              outlined
              dense
              v-model="newItem.label"
            />
          </v-col>
          <v-col cols="12">
            <v-card-subtitle
              @click="setting = !setting"
              class="w-100 d-flex align-items-center justify-content-between cursor-pointer"
            >
              <div>
                <v-icon class="ml-2">mdi-cog</v-icon>
                {{ $t("advanced_settings") }}
              </div>
              <div>
                <v-icon v-if="!setting">mdi-chevron-down</v-icon>
                <v-icon v-else>mdi-chevron-up</v-icon>
              </div>
            </v-card-subtitle>
          </v-col>
          <v-col v-if="setting" cols="12" md="6">
            <v-combobox
              v-model="newItem.items[0].setting.regex"
              dense
              outlined
              :label="$t('select_input_pattern')"
              :placeholder="$t('select_input_pattern_placeholder')"
              :items="regex"
              item-value="value"
              item-text="text"
            />
          </v-col>

          <v-col v-if="setting" cols="12" md="6">
            <v-text-field
              v-model="newItem.items[0].setting.max"
              type="number"
              :label="$t('max_characters')"
              outlined
              :rules="[(v) => v > 0 || v == null || $t('min_input_error')]"
              dense
            />
          </v-col>
          <v-col v-if="setting" cols="12" md="6">
            <v-text-field
              :rules="[(v) => v > 0 || v == null || $t('min_input_error')]"
              v-model="newItem.items[0].setting.min"
              outlined
              type="number"
              :label="$t('min_characters')"
              dense
            />
          </v-col>
        </v-row>
      </v-card>
    </custom-popup>
  </v-card>
</template>

<script>
import IconPicker from "@/view/components/IconPicker.vue";
import { mapActions, mapState } from "vuex";
import _ from "lodash";
import i18n from "../../../i18n";

export default {
  components: { IconPicker },
  name: "consultationNew",
  props: {
    value: Object,
    info: Object,
  },
  data() {
    return {
      i18n,
      defaultItems: [
        {
          label: i18n.t("name_field"),
          id: 2050,
          active: false,
          action: "mdi-rename",
          items: [
            {
              title: i18n.t("field_type"),
              optional: false,
              action: true,
              type: "text",
              setting: {
                regex: { text: i18n.t("persian_limit"), value: "persian" },
                min: null,
                max: null,
              },
            },
          ],
          feedAction: "",
          title: i18n.t("name_field"),
        },
        {
          label: i18n.t("family_name"),
          id: 2051,
          active: false,
          action: "mdi-rename",
          items: [
            {
              title: i18n.t("field_type"),
              optional: false,
              action: true,
              type: "text",
              setting: {
                regex: { text: i18n.t("persian_limit"), value: "persian" },
                min: null,
                max: null,
              },
            },
          ],
          title: i18n.t("family_name"),
          feedAction: "",
        },
        {
          label: i18n.t("mobile_number"),
          id: 2052,
          active: false,
          action: "mdi-cellphone",
          items: [
            {
              title: i18n.t("field_type"),
              optional: false,
              action: true,
              type: "text",
              setting: {
                regex: { text: i18n.t("number_limit"), value: "mobile" },
                min: null,
                max: null,
              },
            },
          ],
          title: i18n.t("mobile_number"),
          feedAction: "",
        },
        {
          label: i18n.t("national_code"),
          id: 2053,
          active: false,
          action: "mdi-numeric-9-plus-box-multiple-outline",
          items: [
            {
              title: i18n.t("field_type"),
              optional: false,
              action: true,
              type: "text",
              setting: {
                regex: { text: i18n.t("meli_code"), value: "meliCode" },
                min: null,
                max: null,
              },
            },
          ],
          title: i18n.t("national_code"),
          feedAction: "",
        },
        {
          label: null,
          id: 2054,
          active: false,
          action: "mdi-email",
          items: [
            {
              title: i18n.t("field_type"),
              optional: false,
              action: true,
              type: "text",
              setting: {
                regex: { text: i18n.t("email_limit"), value: "email" },
                min: null,
                max: null,
              },
            },
          ],
          title: i18n.t("email"),
          feedAction: "",
        },
        {
          label: i18n.t("address"),
          id: 2055,
          active: false,
          action: "mdi-map-marker",
          items: [
            {
              title: i18n.t("field_type"),
              optional: false,
              action: true,
              type: "text",
              setting: {
                regex: { text: i18n.t("persian_limit"), value: "persian" },
                min: null,
                max: null,
              },
            },
          ],
          title: i18n.t("address"),
          feedAction: "",
        },
        {
          label: i18n.t("description"),
          id: 2056,
          active: false,
          action: "mdi-text-box",
          items: [
            {
              title: i18n.t("field_type"),
              optional: false,
              action: true,
              type: "textarea",
              setting: {
                regex: { text: i18n.t("persian_limit"), value: "persian" },
                min: null,
                max: null,
              },
            },
          ],
          title: i18n.t("description"),
          feedAction: "",
        },
        {
          label: i18n.t("dropdown_field"),
          id: 2057,
          active: false,
          action: "mdi-form-select",
          items: [
            {
              title: i18n.t("field_type"),
              optional: false,
              action: true,
              type: "select",
              label: "",
              selectItems: [],
              setting: { regex: {}, min: null, max: null },
            },
          ],
          title: i18n.t("dropdown_field"),
          feedAction: "",
        },
      ],

      items: [],

      newItem: {
        label: null,
        active: false,
        action: "mdi-rename",
        items: [
          {
            title: i18n.t("field_type"),
            optional: false,
            action: true,
            type: null,
            label: "",
            selectItems: [],
            setting: { regex: [], min: null, max: null },
          },
        ],
        feedAction: "",
        title: null,
      },

      dialog: false,

      loading: false,

      oldItemsSnapshot: [],
      changed: true,

      regex: [
        { text: i18n.t("persian_limit"), value: "persian" },
        { text: i18n.t("number_limit"), value: "number" },
        { text: i18n.t("english_limit"), value: "english" },
        { text: i18n.t("mix_per_eng_limit"), value: "mixPerEng" },
        { text: i18n.t("email_limit"), value: "email" },
        { text: i18n.t("mobile_number"), value: "mobile" },
        { text: i18n.t("national_code"), value: "meliCode" },
        { text: i18n.t("site_limit"), value: "site" },
      ],

      setting: false,

      conform: false,
    };
  },

  mounted() {
    this.fetch();
  },

  methods: {
    ...mapActions("local", ["updateQrInfoChange"]),
    removeData(index, indexOf, indexChild) {
      this.items[indexOf].items[indexChild].selectItems = this.items[
        indexOf
      ].items[indexChild].selectItems.filter((item) => item !== index);
    },

    addNewItem(e) {
      let QRID = this.qr_id ?? this.info?.id;

      if (e == "submit") {
        let newItems = [...this.items];
        this.newItem.items[0].type = this.newItem.items[0].type.value;
        this.newItem.id = newItems.length + 1;
        newItems.push(this.newItem);
        try {
          this.loading = true;
          this.$api
            .post("/v1/testQr/application/forms", {
              qr_id: QRID,
              data: newItems,
            })
            .then(() => {
              this.$toast.success(i18n.t("form_added_successfully"));
              this.fetch();
            })
            .finally(() => {
              this.loading = false;
              this.newItem = {
                label: null,
                active: false,
                action: "mdi-rename",
                items: [
                  {
                    title: i18n.t("field_type"),
                    optional: false,
                    action: true,
                    type: null,
                    label: "",
                    selectItems: [],
                    setting: { regex: {}, min: null, max: null },
                  },
                ],
                feedAction: "",
                title: null,
              };
            });
        } catch {}
      }
    },

    // getitems
    fetch() {
      let QRID = this.qr_id ?? this.info?.id;
      this.loading = true;
      this.$api
        .get(`/v1/testQr/application/forms?qr_id=${QRID}`)
        .then(({ data }) => {
          this.items = data.data.data;
          this.changed = false;
        })
        .catch((err) => console.log(err))
        .finally(() => {
          this.loading = false;
          this.updateQrInfoChange();
        });
    },

    // update
    update() {
      try {
        let QRID = this.qr_id ?? this.info?.id;
        this.loading = true;
        this.$api
          .post("/v1/testQr/application/forms", {
            qr_id: QRID,
            data: this.items ?? [],
          })
          .then(() => {
            this.$toast.success(i18n.t("form_updated_successfully"));
            this.fetch();
            this.updateQrInfoChange();
          })
          .finally(() => {
            this.loading = false;
          });
      } catch {}
    },

    // delete
    async deleted(id) {
      let QRID = this.qr_id ?? this.info?.id;

      if (await this.$modal.yesNo(i18n.t("delete_item_confirm"))) {
        if (id !== "all") {
          let deletedItems = this.items.filter((item) => item.id !== id);
          try {
            this.loading = true;
            this.$api
              .post("/v1/testQr/application/forms", {
                qr_id: QRID,
                data: deletedItems,
              })
              .then(() => {
                this.$toast.success(i18n.t("form_updated_successfully"));
                this.fetch();
              })
              .finally(() => {
                this.loading = false;
              });
          } catch {}
        } else {
          try {
            this.loading = true;
            this.$api
              .post("/v1/testQr/application/forms", {
                qr_id: QRID,
                data: [],
              })
              .then(() => {
                this.$toast.success(i18n.t("form_updated_successfully"));
                this.fetch();
              })
              .finally(() => {
                this.loading = false;
              });
          } catch {}
        }
      }
    },

    // add Default Item
    addDefaults() {
      let QRID = this.qr_id ?? this.info?.id;
      let items = !!this.items ? [...this.items] : [];
      const existingIds = Array.isArray(this.items)
        ? new Set(this.items.map((item) => item.id))
        : new Set();

      const newItems = this.defaultItems.filter((defaultItem) => {
        return !existingIds.has(defaultItem.id);
      });

      let newItemsWithDefault = [...items, ...newItems];
      try {
        this.loading = true;
        this.$api
          .post("/v1/testQr/application/forms", {
            qr_id: QRID,
            data: newItemsWithDefault,
          })
          .then(() => {
            this.$toast.success(i18n.t("default_form_added_successfully"));
            this.fetch();
          })
          .finally(() => {
            this.loading = false;
          });
      } catch {}
    },
  },

  computed: {
    ...mapState("local", ["qr_id", "qrInfo"]),
    domainName() {
      return this.$route.params.name;
    },
    qrId() {
      return this.$route.params?.id?.replace('?gateway=true','');
    },
    hasMissingDefaults() {
      const existingIds = Array.isArray(this.items)
        ? new Set(this.items.map((item) => item.id))
        : new Set();
      return this.defaultItems.some(
        (defaultItem) => !existingIds.has(defaultItem.id),
      );
    },

    computedValue: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
  },

  watch: {
    "$i18n.locale": {
      handler() {
        this.$forceUpdate();
      },
      immediate: true,
      deep: true,
    },
    items: {
      handler(val) {
        if (!_.isEqual(val, this.oldItemsSnapshot)) {
          this.oldItemsSnapshot = _.cloneDeep(val);
          this.changed = true;
        } else {
          this.changed = false;
        }
      },
      deep: true,
    },

    conform(val) {
      if (val) {
        this.computedValue.conform = true;
      } else {
        this.computedValue.conform = false;
      }
    },

    info: {
      deep: true,
      immediate: true,
      handler(val) {
        if (val) {
          this.fetch();
        }
      },
    },
  },
};
</script>

<style></style>
